import { parseCitationRef } from '@nutians/shared';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router';
import remarkGfm from 'remark-gfm';
import type { ChatMessageState } from '../../hooks/useChatStream.js';
import { ClarificationCard } from './ClarificationCard.js';
import { PendingActionCard } from './PendingActionCard.js';

// ---- Citation link ----------------------------------------------------------

function CitationLink({ href, children }: { href: string; children: React.ReactNode }) {
  const cls =
    'inline font-medium text-leaf underline underline-offset-2 ' +
    'hover:text-leaf-deep dark:text-mist dark:hover:text-leaf-deep';

  if (href.startsWith('days/')) {
    const date = href.slice(5);
    return (
      <Link to={`/?date=${date}`} className={cls} title={href}>
        {children}
      </Link>
    );
  }

  // week, month, entry, metric — no deep-link page yet; render as styled text
  return (
    <span className={cls} title={href}>
      {children}
    </span>
  );
}

// ---- Markdown renderer (assistant messages only) ----------------------------

// Custom components passed to ReactMarkdown. Each element maps to a styled
// equivalent so the output fits the chat bubble's text-sm context.
const mdComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  // Citation links use app routing; regular links open externally.
  a({ href, children }) {
    const resolvedHref = href ?? '';
    if (parseCitationRef(resolvedHref) !== null) {
      return <CitationLink href={resolvedHref}>{children}</CitationLink>;
    }
    return (
      <a
        href={resolvedHref}
        target="_blank"
        rel="noopener noreferrer"
        className="text-leaf underline hover:text-leaf-deep dark:text-mist"
      >
        {children}
      </a>
    );
  },

  // Tables: horizontal scroll wrapper so wide nutrient tables don't overflow.
  table({ children }) {
    return (
      <div className="overflow-x-auto my-2">
        <table className="min-w-full border-collapse text-xs">{children}</table>
      </div>
    );
  },
  th({ children }) {
    return (
      <th className="border border-husk bg-husk px-2 py-1 text-left font-semibold dark:border-ink/20 dark:bg-shell">
        {children}
      </th>
    );
  },
  td({ children }) {
    return <td className="border border-husk px-2 py-1 dark:border-ink/20">{children}</td>;
  },

  // Headings — sized down to fit a chat bubble's text-sm baseline.
  h1({ children }) {
    return <h1 className="text-base font-bold mt-3 mb-1">{children}</h1>;
  },
  h2({ children }) {
    return <h2 className="text-sm font-bold mt-2 mb-1">{children}</h2>;
  },
  h3({ children }) {
    return <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>;
  },

  // Lists.
  ul({ children }) {
    return <ul className="list-disc list-inside my-1 space-y-0.5">{children}</ul>;
  },
  ol({ children }) {
    return <ol className="list-decimal list-inside my-1 space-y-0.5">{children}</ol>;
  },

  // Inline code vs. fenced code blocks.
  code({ className, children }) {
    const isBlock = Boolean(className);
    if (isBlock) {
      return (
        <code className="block font-mono text-xs bg-shell text-ink dark:bg-leaf rounded px-3 py-2 my-2 overflow-x-auto whitespace-pre">
          {children}
        </code>
      );
    }
    return (
      <code className="font-mono text-xs bg-husk text-ink dark:bg-shell dark:text-ink rounded px-1">
        {children}
      </code>
    );
  },

  // Paragraphs: keep leading-relaxed in the bubble context.
  p({ children }) {
    return <p className="my-1 leading-relaxed">{children}</p>;
  },
};

function MarkdownContent({ text }: { text: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
      {text}
    </ReactMarkdown>
  );
}

// ---- Plain text (user messages) ---------------------------------------------

function PlainText({ text }: { text: string }) {
  return (
    <span>
      {text.split('\n').map((line, i, arr) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: line splits are order-stable
        <span key={i}>
          {line}
          {i < arr.length - 1 && <br />}
        </span>
      ))}
    </span>
  );
}

// ---- Typing indicator -------------------------------------------------------

function TypingIndicator() {
  return (
    <span role="status" className="flex items-center gap-1">
      <span className="sr-only">Typing…</span>
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="inline-block h-2 w-2 animate-pulse rounded-full bg-mist"
          style={{ animationDelay: `${delay}ms` }}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

// ---- Main component ---------------------------------------------------------

interface ChatMessageProps {
  message: ChatMessageState;
  onConfirmAction: (actionId: string, decision: 'accept' | 'reject') => Promise<void>;
  onSelectClarification: (clarificationId: string, selected: unknown[]) => Promise<void>;
}

export function ChatMessage({ message, onConfirmAction, onSelectClarification }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <article
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
      aria-label={isUser ? 'Your message' : 'Assistant message'}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? 'bg-leaf text-white dark:bg-leaf-deep'
            : 'border border-mist/20 bg-mist/10 text-ink'
        }`}
      >
        {/* AI badge — only on assistant messages */}
        {!isUser && (
          <div className="mb-1.5 flex items-center gap-1.5" aria-hidden="true">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-mist px-1.5 py-0.5
                         text-[10px] font-medium text-white"
            >
              <svg className="h-3 w-3" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                <path d="M6 0l1.2 4.8L12 6l-4.8 1.2L6 12 4.8 7.2 0 6l4.8-1.2z" />
              </svg>
              AI
            </span>
          </div>
        )}

        {/* Message body */}
        {message.isStreaming && !message.content ? (
          <TypingIndicator />
        ) : (
          <div>
            {isUser ? (
              <PlainText text={message.content} />
            ) : (
              <MarkdownContent text={message.content} />
            )}
          </div>
        )}

        {/* Stream error */}
        {message.error && (
          <p role="alert" aria-live="assertive" className="mt-2 text-xs text-clay dark:text-clay">
            {message.error}
          </p>
        )}

        {/* Pending action confirmation cards */}
        {message.pendingActions.map((action) => (
          <PendingActionCard
            key={action.id}
            action={action}
            onConfirm={(decision) => onConfirmAction(action.id, decision)}
            readOnly={!message.actionsAreLive}
          />
        ))}

        {/* Clarification request forms (ADR-0028 §4.2) */}
        {message.clarifications.map((clar) => (
          <ClarificationCard
            key={clar.id}
            clarification={clar}
            onSelect={onSelectClarification}
            readOnly={!message.actionsAreLive}
          />
        ))}
      </div>
    </article>
  );
}
